import { useEffect, useRef } from 'react';

const stats = [
  { value: '20+', label: 'Years of Experience', icon: 'ri-history-line' },
  { value: '$500M+', label: 'In Real Estate Sales', icon: 'ri-money-dollar-circle-line' },
  { value: '500+', label: 'Clients Represented', icon: 'ri-team-line' },
  { value: '5★', label: 'Client Satisfaction', icon: 'ri-star-fill' },
];

const reasons = [
  {
    icon: 'ri-map-pin-2-line',
    title: 'Local Lake Tahoe Expertise',
    text: 'Deep knowledge of resort property dynamics, seasonal demand, and long-term value trends specific to the Sierra Nevada alpine market.',
  },
  {
    icon: 'ri-scales-3-line',
    title: 'Precision-Driven Pricing',
    text: 'Data-backed market analysis combined with nuanced negotiation skill to achieve the best possible outcome for every client.',
  },
  {
    icon: 'ri-megaphone-line',
    title: 'Elevated Marketing',
    text: 'Sophisticated positioning and high-impact exposure strategies designed for luxury buyers and sellers in the Tahoe region.',
  },
  {
    icon: 'ri-vip-crown-line',
    title: 'Concierge-Level Service',
    text: 'Personalized, white-glove guidance from initial consultation through closing — always with discretion and professionalism.',
  },
  {
    icon: 'ri-shake-hands-line',
    title: 'Trusted Advisory',
    text: 'Proven guidance through complex real estate transactions, with a track record of success in the Lake Tahoe and Sierra Nevada markets.',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Results-Driven Execution',
    text: 'A client-first approach focused on measurable outcomes — sold faster, at the right price, and with minimal stress.',
  },
];

export default function WhyChooseUs() {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const targets = [leftRef.current, rightRef.current];
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.05 }
    );
    targets.forEach((el, i) => {
      if (!el) return;
      el.style.opacity = '0';
      el.style.transform = 'translateY(40px)';
      el.style.transition = `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${i * 0.15}s, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${i * 0.15}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative z-10 bg-warm-white py-28 px-4 md:px-8 rounded-t-[2.5rem] -mt-8">
      {/* Subtle dot texture */}
      <div
        className="absolute inset-0 opacity-[0.012] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #2A2A2A 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Left: agent photo + bio */}
          <div ref={leftRef} className="lg:w-2/5">
            <p className="text-[10px] tracking-[0.55em] uppercase text-gold mb-4">Why Cam Kirkwood</p>
            <h2 className="font-serif text-4xl md:text-6xl text-charcoal font-light leading-tight mb-6">
              The Difference <em className="italic">Expertise</em> Makes
            </h2>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-16 h-px bg-gold/40" />
              <div className="w-1.5 h-1.5 rotate-45 bg-gold/60" />
              <div className="w-16 h-px bg-gold/40" />
            </div>

            {/* Agent image */}
            <div className="relative">
              <div className="w-full h-[460px] rounded-2xl overflow-hidden">
                <img
                  src="https://www.camkirkwood.com/images/cam-kirkwood.jpg"
                  alt="Cam Kirkwood – Luxury Real Estate Specialist in Lake Tahoe & Sierra Nevada"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Gold border offset */}
              <div className="absolute -bottom-3 -right-3 w-full h-full border border-gold/20 rounded-2xl -z-10 pointer-events-none" />
              {/* Compass badge */}
              <div
                className="absolute bottom-5 left-5 bg-white/95 px-5 py-3 rounded-xl"
                style={{ boxShadow: '0 4px 24px rgba(42,42,42,0.14)' }}
              >
                <img
                  src="https://www.camkirkwood.com/images/logo-compass_w344.png"
                  alt="Compass Real Estate"
                  className="h-5 object-contain opacity-75"
                />
              </div>
              {/* Stats overlay badge */}
              <div
                className="absolute top-5 right-5 bg-charcoal/90 backdrop-blur-sm px-4 py-3 rounded-xl"
                style={{ border: '1px solid rgba(196,169,98,0.2)' }}
              >
                <p className="font-serif text-xl text-gold font-light leading-none">$500M+</p>
                <p className="text-[9px] tracking-[0.2em] uppercase text-warm-white/50 mt-0.5">In Sales</p>
              </div>
            </div>

            {/* Agent contact */}
            <div className="mt-10">
              <p className="font-serif text-2xl text-charcoal font-light">Cam Kirkwood</p>
              <p className="text-[10px] tracking-[0.3em] uppercase text-gold mt-1 mb-5">
                Luxury Real Estate Specialist — Lake Tahoe &amp; Sierra Nevada
              </p>
              <div className="flex flex-col gap-3">
                <a href="tel:5617146589" className="flex items-center gap-3 text-sm text-charcoal hover:text-gold transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-9 h-9 flex items-center justify-center border border-gold/30 rounded-lg">
                    <i className="ri-phone-line text-gold text-sm" />
                  </div>
                  561-714-6589
                </a>
                <a href="mailto:cam@camkirkwood.com" className="flex items-center gap-3 text-sm text-charcoal hover:text-gold transition-colors cursor-pointer">
                  <div className="w-9 h-9 flex items-center justify-center border border-gold/30 rounded-lg">
                    <i className="ri-mail-line text-gold text-sm" />
                  </div>
                  cam@camkirkwood.com
                </a>
              </div>
            </div>
          </div>

          {/* Right: stats + reasons */}
          <div ref={rightRef} className="lg:w-3/5">
            {/* Stats bar */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {stats.map((s) => (
                <div
                  key={s.label}
                  className="rounded-xl p-5 text-center"
                  style={{ background: '#2A2A2A' }}
                >
                  <div className="w-8 h-8 flex items-center justify-center mx-auto mb-3 rounded-lg bg-gold/15">
                    <i className={`${s.icon} text-gold text-sm`} />
                  </div>
                  <p className="font-serif text-3xl text-gold font-light leading-none">{s.value}</p>
                  <p className="text-warm-white/45 text-[9px] tracking-[0.15em] uppercase mt-2 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Authority statement */}
            <div
              className="rounded-2xl p-6 mb-8"
              style={{ background: 'rgba(196,169,98,0.06)', border: '1px solid rgba(196,169,98,0.18)' }}
            >
              <i className="ri-double-quotes-l text-gold/30 text-4xl block mb-3" />
              <p className="font-serif text-lg text-charcoal font-light leading-relaxed italic">
                "With over two decades navigating the Lake Tahoe and Sierra Nevada luxury market, Cam Kirkwood has built an unmatched record of results — consistently delivering for buyers, sellers, and investors at every level."
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {reasons.map((r) => (
                <div key={r.title} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg border border-gold/20 bg-gold/6">
                    <i className={`${r.icon} text-gold text-lg`} />
                  </div>
                  <div>
                    <h3 className="font-serif text-base text-charcoal font-medium mb-1.5">{r.title}</h3>
                    <p className="text-stone text-sm leading-relaxed">{r.text}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Mid CTA */}
            <div className="mt-10 pt-8 border-t border-cream">
              <a
                href="#consultation"
                className="inline-flex items-center gap-3 whitespace-nowrap px-8 py-4 bg-charcoal text-white text-[11px] tracking-[0.2em] uppercase font-medium cursor-pointer rounded-md hover:bg-charcoal/80 transition-all duration-300"
              >
                <span>Request Private Consultation</span>
                <i className="ri-arrow-right-line text-gold" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
